import { normalizeNfc } from "../../../utils/text";

/**
 * ノートの Markdown 補助ユーティリティ。
 *
 * - wikilink（`[[ノートタイトル]]`）を内部リンクへ変換する
 * - リッチ編集のシリアライズが付けるバックスラッシュエスケープを復元する
 *   （normalizeNoteMarkdown。wikilink・見出し・強調などの記法を守る）
 * - 貼り付けた Markdown の見出し記法ゆれを正規化する（normalizePastedMarkdown）
 * - 見出しから目次（TOC）を抽出する
 */

/**
 * wikilink 記法。タイトルに改行・角括弧は含められない。
 * リッチ編集（Tiptap）の Markdown シリアライズは `[` `]` を `\[` `\]` に
 * エスケープするため、エスケープ済みの `\[\[タイトル\]\]` も許容する
 * （過去にリッチ編集で保存されて壊れた本文も再保存なしでリンク化するため）。
 */
const WIKILINK_RE = /\\?\[\\?\[([^[\]\n]+?)\\?\]\\?\]/g;

/**
 * prosemirror-markdown のシリアライザがエスケープする文字
 * （` * \ ~ [ ] _）の、エスケープ済み表現にマッチする。
 */
const ESCAPED_MD_CHAR_RE = /\\([`*\\~[\]_])/g;

const unescapeMarkdownChars = (text: string): string =>
  text.replace(ESCAPED_MD_CHAR_RE, "$1");

/** 未解決 wikilink の href。スタイル分けとクリック無効化に使う */
export const MISSING_NOTE_HREF = "#note-missing";

/** 内部ノートリンクの href prefix。クリックは委任ハンドラで解決する */
export const NOTE_HREF_PREFIX = "#note-";

/**
 * `[[タイトル]]` を title → note_id マップで Markdown リンクへ変換する。
 * 解決できたものは `[タイトル](#note-<id>)`、できなかったものは
 * `[タイトル](#note-missing)` になる（表示側で別スタイルにする）。
 */
export const resolveWikilinks = (
  content: string,
  wikilinkMap: Record<string, string>,
): string =>
  content.replace(WIKILINK_RE, (_match, rawTitle: string) => {
    const title = normalizeNfc(unescapeMarkdownChars(rawTitle).trim());
    const noteId = wikilinkMap[title];
    const href = noteId ? `${NOTE_HREF_PREFIX}${noteId}` : MISSING_NOTE_HREF;
    return `[${title}](${href})`;
  });

/** フェンスコードブロックの開始/終了行（``` / ~~~） */
const FENCE_RE = /^\s*(```|~~~)/;

/**
 * 行頭のブロック記法エスケープを復元する。
 * prosemirror-markdown の esc(startOfLine) は行頭の見出し（`\###`）・
 * リスト/引用マーカー（`\-` `\+` `\*` `\>`）・番号付きリスト（`1\. `）を
 * エスケープするため、それぞれ元の記法へ戻す。
 */
const unescapeLineStart = (line: string): string =>
  line
    .replace(/^(\s*)\\(#{1,6})(\s|$)/, "$1$2$3")
    .replace(/^\\([-+*>])/, "$1")
    .replace(/^(\s*\d+)\\\.(\s)/, "$1.$2");

/**
 * インラインコード（`code`）を保護しつつ、コード以外の部分にだけ
 * 変換関数を適用する。
 */
const mapOutsideInlineCode = (
  line: string,
  transform: (segment: string) => string, // eslint-disable-line no-unused-vars
): string =>
  line
    .split(/(`[^`]*`)/g)
    .map((seg, i) => (i % 2 === 1 ? seg : transform(seg)))
    .join("");

/**
 * 行単位の変換をフェンスコードブロックの外側にだけ適用する共通処理。
 * コードブロック内はユーザーが書いたとおりに保持する。
 */
const mapOutsideFences = (
  markdown: string,
  transform: (line: string) => string, // eslint-disable-line no-unused-vars
): string => {
  let inFence = false;
  return markdown
    .split("\n")
    .map((line) => {
      if (FENCE_RE.test(line)) {
        inFence = !inFence;
        return line;
      }
      return inFence ? line : transform(line);
    })
    .join("\n");
};

/**
 * リッチ編集（Tiptap / prosemirror-markdown）のシリアライズが付ける
 * バックスラッシュエスケープを復元する（保存・ソース表示前の正規化）。
 *
 * リッチ編集ではプレーンテキストとして残った `###見出し` や `**強調**`・
 * `[[wikilink]]` が `\###` `\*\*` `\[\[` にエスケープされ、閲覧側で
 * 記法として解釈されなくなる。書き手の意図は「Markdown 記法として
 * 表示される」side なので、エスケープはすべて外して保存する
 * （リッチ編集で意図的にリテラルの `*` 等を見せたいケースより、
 * 貼り付けた Markdown が壊れるデメリットのほうが大きい）。
 * コードブロック・インラインコード内は変更しない。
 */
export const normalizeNoteMarkdown = (markdown: string): string =>
  mapOutsideFences(markdown, (line) =>
    mapOutsideInlineCode(unescapeLineStart(line), unescapeMarkdownChars),
  );

/**
 * 他所からコピーした Markdown テキストを貼り付ける際の正規化。
 *
 * - ATX 見出し記号の直後の全角スペース（U+3000）を半角スペースへ
 *   （CommonMark は半角スペース/タブのみ見出し区切りとして認める）
 * - `##見出し` のようにスペースが無い ATX 見出しへスペースを補完
 *   （`#タグ` のようなハッシュタグ誤変換を避けるため ## 以上のみ）
 * - 過去の不具合でエスケープされたまま保存・コピーされたソース
 *   （`\###` `\*\*` 等）の復元（normalizeNoteMarkdown と同じ）
 */
export const normalizePastedMarkdown = (text: string): string =>
  mapOutsideFences(text, (line) =>
    mapOutsideInlineCode(
      unescapeLineStart(
        line
          .replace(/^(#{1,6})\u3000+/, "$1 ")
          .replace(/^(#{2,6})([^#\s])/, "$1 $2"),
      ),
      unescapeMarkdownChars,
    ),
  );

/** クリックされた a 要素からノート ID を取り出す（内部リンクでなければ null） */
export const extractNoteIdFromHref = (href: string | null): string | null => {
  if (!href || !href.startsWith(NOTE_HREF_PREFIX)) return null;
  const noteId = href.slice(NOTE_HREF_PREFIX.length);
  return noteId && noteId !== "missing" ? noteId : null;
};

export interface TocItem {
  /** 見出しレベル（1〜3） */
  level: number;
  text: string;
  /** 本文中で何番目の見出しか（スクロール先の特定に使う） */
  index: number;
}

/**
 * Markdown 本文から h1〜h3 の見出しを抽出して目次を作る。
 * フェンスコードブロック内の `#` は無視する。
 */
export const extractToc = (content: string): TocItem[] => {
  const items: TocItem[] = [];
  let inFence = false;
  let index = 0;
  for (const line of content.split("\n")) {
    if (/^\s*(```|~~~)/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const match = /^(#{1,3})\s+(.+)$/.exec(line);
    if (match) {
      items.push({
        level: match[1].length,
        text: match[2].replace(/[#*`~[\]]/g, "").trim(),
        index,
      });
      index += 1;
    }
  }
  return items;
};
